import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Search, ShieldCheck, ShieldOff, UserCog, Loader2, MoreVertical,
  KeyRound, Unlock, StickyNote, Pencil, Trash2, Sparkles,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FILTERS = [
  { id: '', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'deactivated', label: 'Deactivated' },
  { id: 'locked', label: 'Locked' },
  { id: 'idle', label: 'Idle 30d+' },
  { id: 'deleted', label: 'Deleted' },
];

function relativeWhen(iso) {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch { return iso; }
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [viewerId, setViewerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [actingId, setActingId] = useState(null);

  // Modal state
  const [editUser, setEditUser] = useState(null);   // {user_id,name,email}
  const [editForm, setEditForm] = useState({ name: '', email: '' });
  const [editBusy, setEditBusy] = useState(false);

  const [notesUser, setNotesUser] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [notesBusy, setNotesBusy] = useState(false);

  const load = async (q = search, status = statusFilter) => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      const res = await axios.get(`${API_URL}/api/admin/users`, { params, withCredentials: true });
      let userList = res.data.users || [];
      setViewerId(res.data.viewer_user_id);
      // Fetch usage summary in parallel + merge by user_id
      try {
        const usageRes = await axios.get(`${API_URL}/api/admin/studio-users/usage-summary`, { withCredentials: true });
        const usageMap = {};
        (usageRes.data.users || []).forEach((u) => { usageMap[u.user_id] = u; });
        userList = userList.map((u) => {
          const usage = usageMap[u.user_id];
          if (!usage) return u;
          return {
            ...u,
            usage_total: usage.total,
            usage_scripts: usage.scripts,
            usage_posts: usage.posts,
            usage_carousels: usage.carousels,
            usage_threads: usage.threads,
          };
        });
      } catch {/* non-fatal */}
      setUsers(userList);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    load(search.trim(), statusFilter);
  };

  const applyFilter = (id) => {
    setStatusFilter(id);
    load(search.trim(), id);
  };

  const changeRole = async (user, newRole) => {
    if (user.user_id === viewerId && newRole !== 'admin') {
      toast.error('You cannot demote yourself');
      return;
    }
    if (!window.confirm(`Set ${user.email} role to "${newRole}"?`)) return;
    setActingId(user.user_id);
    try {
      await axios.patch(`${API_URL}/api/admin/users/${user.user_id}/role`, { role: newRole }, { withCredentials: true });
      toast.success(`Role updated to ${newRole}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setActingId(null);
    }
  };

  const toggleActive = async (user) => {
    if (user.user_id === viewerId && user.is_active) {
      toast.error('You cannot deactivate your own account');
      return;
    }
    const next = !user.is_active;
    if (!window.confirm(`${next ? 'Activate' : 'Deactivate'} ${user.email}?`)) return;
    setActingId(user.user_id);
    try {
      await axios.patch(`${API_URL}/api/admin/users/${user.user_id}/status`, { is_active: next }, { withCredentials: true });
      toast.success(next ? 'User activated' : 'User deactivated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setActingId(null);
    }
  };

  const sendResetEmail = async (user) => {
    if (!window.confirm(`Send a password-reset link to ${user.email}?`)) return;
    setActingId(user.user_id);
    try {
      await axios.post(`${API_URL}/api/admin/users/${user.user_id}/reset-password`, {}, { withCredentials: true });
      toast.success(`Reset link sent to ${user.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setActingId(null);
    }
  };

  const unlockUser = async (user) => {
    setActingId(user.user_id);
    try {
      await axios.post(`${API_URL}/api/admin/users/${user.user_id}/unlock`, {}, { withCredentials: true });
      toast.success('Account unlocked');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock');
    } finally {
      setActingId(null);
    }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ name: u.name || '', email: u.email || '' });
  };
  const saveEdit = async () => {
    setEditBusy(true);
    try {
      await axios.patch(`${API_URL}/api/admin/users/${editUser.user_id}`, editForm, { withCredentials: true });
      toast.success('User updated');
      setEditUser(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save changes');
    } finally {
      setEditBusy(false);
    }
  };

  const openNotes = async (u) => {
    setNotesUser(u);
    setNotes([]);
    setNotesBusy(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/users/${u.user_id}/notes`, { withCredentials: true });
      setNotes(res.data.notes || []);
    } finally { setNotesBusy(false); }
  };
  const addNote = async () => {
    const note = newNote.trim();
    if (!note) return;
    setNotesBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/users/${notesUser.user_id}/notes`, { note }, { withCredentials: true });
      setNotes([res.data.note, ...notes]);
      setNewNote('');
      toast.success('Note added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add note');
    } finally {
      setNotesBusy(false);
    }
  };
  const removeNote = async (note_id) => {
    if (!window.confirm('Delete this note?')) return;
    setNotesBusy(true);
    try {
      await axios.delete(`${API_URL}/api/admin/users/${notesUser.user_id}/notes/${note_id}`, { withCredentials: true });
      setNotes(notes.filter((n) => n.note_id !== note_id));
    } catch (err) {
      toast.error('Failed to delete note');
    } finally {
      setNotesBusy(false);
    }
  };

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pendingRequests, setPendingRequests] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [purgeBusy, setPurgeBusy] = useState(false);

  const loadPendingRequests = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/studio-users/pending-requests`, { withCredentials: true });
      setPendingRequests(r.data.requests || []);
    } catch { /* non-fatal */ }
  };

  useEffect(() => { loadPendingRequests(); }, []);

  const toggleSelect = (uid) => {
    const next = new Set(selectedIds);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setSelectedIds(next);
  };

  const bulkAccess = async (grant) => {
    const ids = [...selectedIds].filter((id) => {
      const u = users.find((x) => x.user_id === id);
      return u && u.role !== 'admin' && u.role !== 'super_admin';
    });
    if (ids.length === 0) { toast.error('Select at least one non-admin user'); return; }
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/studio-users/bulk-access`,
        { user_ids: ids, is_studio_approved: grant }, { withCredentials: true });
      toast.success(`${grant ? 'Granted' : 'Revoked'} Studio access for ${r.data.updated} user(s)`);
      setSelectedIds(new Set());
      await load();
      await loadPendingRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBulkBusy(false); }
  };

  const purgeTestAccounts = async () => {
    setPurgeBusy(true);
    try {
      // Step 1: dry-run preview
      const dry = await axios.post(`${API_URL}/api/admin/studio-users/purge-test-accounts`,
        { dry_run: true }, { withCredentials: true });
      const n = dry.data.would_delete;
      if (n === 0) { toast.info('No test accounts match the purge patterns.'); return; }
      const preview = (dry.data.preview || []).slice(0, 8).map((p) => `- ${p.email}`).join('\n');
      // eslint-disable-next-line no-restricted-globals
      if (!confirm(`Purge ${n} test account(s)? This is PERMANENT.\n\n${preview}${n > 8 ? `\n... and ${n - 8} more` : ''}`)) return;
      const real = await axios.post(`${API_URL}/api/admin/studio-users/purge-test-accounts`,
        { dry_run: false }, { withCredentials: true });
      toast.success(`Purged ${real.data.deleted} test account(s)`);
      await load();
      await loadPendingRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setPurgeBusy(false); }
  };

  return (
    <AdminLayout title="Users & Roles">
      <div className="mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="admin-users-header">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0F1C] flex items-center gap-2"><UserCog size={22} /> Users & Roles</h1>
          <p className="text-sm text-[#475569] mt-1">Manage admins, clients, and account access. All sensitive actions are logged to Activity.</p>
        </div>
        <form onSubmit={handleSearch} className="flex items-center gap-2" data-testid="admin-users-search-form">
          <div className="relative">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              className="pl-9 w-72"
              data-testid="admin-users-search-input"
            />
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          </div>
          <Button type="submit" className="bg-[#1D4ED8] text-white hover:bg-[#1E40AF]" data-testid="admin-users-search-btn">Search</Button>
          <Button
            type="button"
            onClick={purgeTestAccounts}
            disabled={purgeBusy}
            variant="outline"
            className="text-rose-600 border-rose-200 hover:bg-rose-50"
            data-testid="admin-purge-test-accounts-btn"
          >
            {purgeBusy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Trash2 size={14} className="mr-1" />}
            Purge test accounts
          </Button>
        </form>
      </div>

      {/* Pending Studio Access Requests */}
      {pendingRequests.length > 0 && (
        <div className="mb-6 bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-2xl p-4" data-testid="pending-requests-panel">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold uppercase tracking-wider text-amber-900 flex items-center gap-2">
              <Sparkles size={14} /> {pendingRequests.length} Pending Social Studio request{pendingRequests.length === 1 ? '' : 's'}
            </h2>
          </div>
          <ul className="divide-y divide-amber-200">
            {pendingRequests.map((r) => (
              <li key={r.user_id} className="py-2.5 flex items-center gap-3 flex-wrap" data-testid={`pending-row-${r.user_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#0A0F1C]">{r.name || `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.email}</div>
                  <div className="text-xs text-[#475569]">{r.email} · {r.phone || 'no phone'} · {r.requested_at?.slice(0, 16).replace('T', ' ')}</div>
                  {r.request_note && <div className="text-xs text-[#64748B] mt-0.5 italic">"{r.request_note}"</div>}
                </div>
                <button type="button" onClick={async () => {
                  try {
                    await axios.patch(`${API_URL}/api/admin/studio-users/${r.user_id}/access`,
                      { is_studio_approved: true }, { withCredentials: true });
                    toast.success(`Granted access to ${r.email}`);
                    await load();
                    await loadPendingRequests();
                  } catch (e) { toast.error(e.response?.data?.detail || e.message); }
                }} data-testid={`pending-approve-${r.user_id}`}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg">
                  Approve
                </button>
                <button type="button" onClick={async () => {
                  try {
                    await axios.patch(`${API_URL}/api/admin/studio-users/${r.user_id}/access`,
                      { is_studio_approved: false }, { withCredentials: true });
                    toast.success(`Denied for ${r.email}`);
                    await load();
                    await loadPendingRequests();
                  } catch (e) { toast.error(e.response?.data?.detail || e.message); }
                }} data-testid={`pending-deny-${r.user_id}`}
                  className="px-3 py-1.5 bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs font-semibold rounded-lg">
                  Deny
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="mb-3 bg-[#0A0F1C] text-white rounded-xl px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid="bulk-action-bar">
          <div className="text-sm font-semibold">{selectedIds.size} selected</div>
          <button type="button" onClick={() => bulkAccess(true)} disabled={bulkBusy} data-testid="bulk-grant-btn"
            className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold rounded-lg disabled:opacity-50">
            {bulkBusy ? '...' : 'Grant Studio access'}
          </button>
          <button type="button" onClick={() => bulkAccess(false)} disabled={bulkBusy} data-testid="bulk-revoke-btn"
            className="px-3 py-1.5 bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold rounded-lg disabled:opacity-50">
            {bulkBusy ? '...' : 'Revoke Studio access'}
          </button>
          <button type="button" onClick={() => setSelectedIds(new Set())} className="ml-auto text-xs underline opacity-80 hover:opacity-100">Clear selection</button>
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2" data-testid="admin-users-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id || 'all'}
            type="button"
            onClick={() => applyFilter(f.id)}
            className={`px-3 py-1.5 text-xs font-bold rounded-full border transition-colors ${statusFilter === f.id ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]' : 'bg-white text-[#475569] border-[#E2E8F0] hover:border-[#1D4ED8] hover:text-[#1D4ED8]'}`}
            data-testid={`admin-users-filter-${f.id || 'all'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead className="bg-[#F8FAFC] text-left text-xs uppercase tracking-wider text-[#475569]">
            <tr>
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  checked={users.length > 0 && selectedIds.size === users.filter((u) => u.role !== 'admin' && u.role !== 'super_admin').length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedIds(new Set(users.filter((u) => u.role !== 'admin' && u.role !== 'super_admin').map((u) => u.user_id)));
                    } else { setSelectedIds(new Set()); }
                  }}
                  data-testid="select-all-checkbox"
                />
              </th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Studio</th>
              <th className="px-4 py-3">Usage</th>
              <th className="px-4 py-3">Last login</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1F5F9]">
            {loading ? (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-[#94A3B8]"><Loader2 size={16} className="inline animate-spin mr-2" /> Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-[#94A3B8]">No users match.</td></tr>
            ) : users.map((u) => (
              <tr key={u.user_id} className={`hover:bg-[#F8FAFC] ${selectedIds.has(u.user_id) ? 'bg-blue-50' : ''}`} data-testid={`admin-user-row-${u.user_id}`}>
                <td className="px-3 py-3 w-8">
                  {u.role !== 'admin' && u.role !== 'super_admin' && (
                    <input
                      type="checkbox"
                      checked={selectedIds.has(u.user_id)}
                      onChange={() => toggleSelect(u.user_id)}
                      data-testid={`select-checkbox-${u.user_id}`}
                    />
                  )}
                </td>
                <td className="px-4 py-3 font-medium text-[#0A0F1C]">
                  <div>{u.first_name || u.name?.split(' ')[0] || '-'} {u.last_name || u.name?.split(' ').slice(1).join(' ') || ''}</div>
                  {u.user_id === viewerId && <span className="text-[10px] font-bold text-[#1D4ED8] uppercase">You</span>}
                  {u.studio_access_request_status === 'pending' && <span className="ml-1 text-[10px] font-bold text-amber-700 uppercase bg-amber-50 px-1.5 py-0.5 rounded">Pending</span>}
                </td>
                <td className="px-4 py-3 text-[#475569]">
                  {u.email}
                  {u.client_id && <a href={`/admin/clients/${u.client_id}`} className="ml-2 text-[10px] text-[#1D4ED8] hover:underline">↗ profile</a>}
                </td>
                <td className="px-4 py-3 text-xs text-[#475569]" data-testid={`admin-user-phone-${u.user_id}`}>{u.phone || '-'}</td>
                <td className="px-4 py-3">
                  <Badge className={u.role === 'admin' ? 'bg-[#1D4ED8] text-white' : 'bg-[#F1F5F9] text-[#475569]'} data-testid={`admin-user-role-${u.user_id}`}>{u.role}</Badge>
                </td>
                <td className="px-4 py-3">
                  {u.is_locked ? (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-600" data-testid={`admin-user-locked-${u.user_id}`}><KeyRound size={12} /> Locked</span>
                  ) : u.is_active === false ? (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600"><ShieldOff size={12} /> Deactivated</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700"><ShieldCheck size={12} /> Active</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs" data-testid={`admin-user-studio-${u.user_id}`}>
                  {u.is_studio_approved ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                      <Sparkles size={10} /> Approved
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                      Locked
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs" data-testid={`admin-user-usage-${u.user_id}`}>
                  {u.usage_total !== undefined ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#0F172A] bg-blue-50 px-2 py-0.5 rounded-full" title={`Scripts: ${u.usage_scripts || 0} · Posts: ${u.usage_posts || 0} · Carousels: ${u.usage_carousels || 0} · Threads: ${u.usage_threads || 0}`}>
                      {u.usage_total} items
                    </span>
                  ) : <span className="text-[#94A3B8]">-</span>}
                </td>
                <td className="px-4 py-3 text-xs text-[#475569]" data-testid={`admin-user-last-login-${u.user_id}`}>{relativeWhen(u.last_login_at)}</td>
                <td className="px-4 py-3 text-xs text-[#94A3B8]">{u.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="outline" data-testid={`admin-user-actions-${u.user_id}`} disabled={actingId === u.user_id}>
                        <MoreVertical size={14} /> Actions
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem onClick={() => openEdit(u)} data-testid={`admin-user-edit-${u.user_id}`}>
                        <Pencil size={14} className="mr-2" /> Edit name / email
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => sendResetEmail(u)} data-testid={`admin-user-reset-${u.user_id}`}>
                        <KeyRound size={14} className="mr-2" /> Send password reset
                      </DropdownMenuItem>
                      {u.is_locked && (
                        <DropdownMenuItem onClick={() => unlockUser(u)} data-testid={`admin-user-unlock-${u.user_id}`}>
                          <Unlock size={14} className="mr-2" /> Unlock account
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => openNotes(u)} data-testid={`admin-user-notes-${u.user_id}`}>
                        <StickyNote size={14} className="mr-2" /> Notes
                      </DropdownMenuItem>
                      {u.role !== 'admin' && (
                        <DropdownMenuItem
                          onClick={async () => {
                            const currentlyApproved = !!u.is_studio_approved;
                            const verb = currentlyApproved ? 'revoke' : 'grant';
                            if (!window.confirm(`${verb === 'grant' ? 'Grant' : 'Revoke'} Social Studio access for ${u.email}?`)) return;
                            setActingId(u.user_id);
                            try {
                              await axios.patch(`${API_URL}/api/admin/studio-users/${u.user_id}/access`,
                                { is_studio_approved: !currentlyApproved },
                                { withCredentials: true });
                              toast.success(`Studio access ${currentlyApproved ? 'revoked' : 'granted'} for ${u.email}`);
                              await load();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || e.message);
                            } finally { setActingId(null); }
                          }}
                          data-testid={`admin-user-studio-access-${u.user_id}`}
                          className={u.is_studio_approved ? 'text-orange-700' : 'text-emerald-700'}
                        >
                          <Sparkles size={14} className="mr-2" />
                          {u.is_studio_approved ? 'Revoke Social Studio access' : 'Grant Social Studio access'}
                        </DropdownMenuItem>
                      )}
                      {u.role === 'client' ? (
                        <DropdownMenuItem onClick={() => changeRole(u, 'admin')} data-testid={`admin-user-make-admin-${u.user_id}`}>
                          <ShieldCheck size={14} className="mr-2" /> Make admin
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          onClick={() => changeRole(u, 'client')}
                          disabled={u.user_id === viewerId}
                          data-testid={`admin-user-make-client-${u.user_id}`}
                        >
                          <ShieldCheck size={14} className="mr-2" /> Demote to client
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => toggleActive(u)}
                        disabled={u.user_id === viewerId}
                        data-testid={`admin-user-toggle-active-${u.user_id}`}
                        className={u.is_active === false ? 'text-emerald-700' : 'text-red-600'}
                      >
                        <Trash2 size={14} className="mr-2" /> {u.is_active === false ? 'Reactivate' : 'Deactivate'}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={async () => {
                          if (!window.confirm(`Delete ${u.email}? This anonymises the email (reversible by Restore).`)) return;
                          setActingId(u.user_id);
                          try {
                            await axios.delete(`${API_URL}/api/admin/users/${u.user_id}`, { withCredentials: true });
                            toast.success('User soft-deleted');
                            load();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to delete');
                          } finally { setActingId(null); }
                        }}
                        disabled={u.user_id === viewerId || u.is_deleted || u.is_seed}
                        data-testid={`admin-user-delete-${u.user_id}`}
                        className="text-red-600"
                      >
                        <Trash2 size={14} className="mr-2" /> {u.is_seed ? 'Delete (seeded)' : 'Delete'}
                      </DropdownMenuItem>
                      {u.is_deleted && (
                        <DropdownMenuItem
                          onClick={async () => {
                            setActingId(u.user_id);
                            try {
                              await axios.post(`${API_URL}/api/admin/users/${u.user_id}/restore`, {}, { withCredentials: true });
                              toast.success('User restored');
                              load();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to restore');
                            } finally { setActingId(null); }
                          }}
                          data-testid={`admin-user-restore-${u.user_id}`}
                          className="text-emerald-700"
                        >
                          <Unlock size={14} className="mr-2" /> Restore
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit modal */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent data-testid="admin-user-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            <DialogDescription>Update name or email. Email changes apply on next login.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">Full name</label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="admin-user-edit-name" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">Email</label>
              <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="admin-user-edit-email" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={editBusy} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" data-testid="admin-user-edit-save">
              {editBusy ? <><Loader2 size={14} className="animate-spin mr-2" /> Saving…</> : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notes modal */}
      <Dialog open={!!notesUser} onOpenChange={(o) => !o && setNotesUser(null)}>
        <DialogContent data-testid="admin-user-notes-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Notes - {notesUser?.email}</DialogTitle>
            <DialogDescription>Private super-admin notes about this account. Only admins can see these.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note…"
                data-testid="admin-user-notes-input"
              />
              <Button onClick={addNote} disabled={notesBusy || !newNote.trim()} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" data-testid="admin-user-notes-add">Add</Button>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {notes.length === 0 && !notesBusy && <p className="text-xs text-[#94A3B8] text-center py-4">No notes yet.</p>}
              {notes.map((n) => (
                <div key={n.note_id} className="border border-[#E2E8F0] rounded-lg p-3 bg-[#F8FAFC]" data-testid={`admin-user-note-${n.note_id}`}>
                  <p className="text-sm text-[#0A0F1C] whitespace-pre-wrap">{n.note}</p>
                  <div className="flex items-center justify-between mt-2 text-[10px] text-[#94A3B8]">
                    <span>{n.author_email} · {relativeWhen(n.created_at)}</span>
                    <button onClick={() => removeNote(n.note_id)} className="text-red-500 hover:text-red-700">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
